import { TimePanelSource } from 'owa-time-panel-bootstrap';
import { ClientItemId } from 'owa-client-ids';
import MeetingRequestMessageType from 'owa-service/lib/contract/MeetingRequestMessageType';
import { action } from 'satcheljs';
import { CalendarEvent } from 'owa-calendar-types';

export const preloadTimePanelData = action('preloadTimePanelData');

/**
 * Opens Time Panel and navigates to Calendar view
 *
 * REMINDER: Verify panel availability first using isTimePanelAvailable util and consider implementing a graceful fallback,
 * e.g. opening deep-link to Calendar surface in new tab
 */
export const openCalendarView = action('openCalendarView', (source: TimePanelSource) => ({
    source,
}));

/**
 * Opens Time Panel and navigates to Tasks view
 *
 * Optionally pre-populates the QuickAdd input with values
 *
 * REMINDER: Verify panel availability first using isTimePanelAvailable util and consider implementing a graceful fallback,
 * e.g. opening deep-link to Tasks module in  new tab
 */
export const openTasksView = action(
    'openTasksView',
    (
        source: TimePanelSource,
        taskSubject?: string,
        taskLinkedEmailId?: string,
        extractionId?: string
    ) => ({
        source,
        taskSubject,
        taskLinkedEmailId,
        extractionId,
    })
);

/**
 * Opens Time Panel and navigates to Event Details view for the requested event
 *
 * REMINDER: Verify panel availability first using isTimePanelAvailable util and consider implementing a graceful fallback,
 * e.g. launching event reading pane popout
 */
export const openEventDetailsView = action(
    'openEventDetailsView',
    (source: TimePanelSource, clientItemId: ClientItemId) => ({
        source,
        clientItemId,
    })
);

/**
 * Opens Time Panel and navigates to Attendee Tracking view for the requested event,
 * with Back button navigating back to Event Details view
 *
 * REMINDER: Verify panel availability first using isTimePanelAvailable util and consider implementing a graceful fallback,
 * e.g. launching event reading pane popout
 */
export const openAttendeeTrackingView = action(
    'openAttendeeTrackingView',
    (source: TimePanelSource, clientItemId: ClientItemId) => ({
        source,
        clientItemId,
    })
);

/**
 * Closes Attendee Tracking view and associated Event Details view if currently open for the requested event
 *
 * Optionally closes Time Panel as well, depending on the scenario and original panel entrypoint
 */
export const closeAttendeeTrackingView = action(
    'closeAttendeeTrackingView',
    (clientItemId: ClientItemId) => ({ clientItemId })
);

/**
 * Opens Time Panel and navigates to Conflicts view for the requested meeting message
 *
 * REMINDER: Verify panel availability first using isTimePanelAvailable util and consider implementing a graceful fallback,
 * e.g. reporting inline string summary of conflicts
 */
export const openConflictsView = action(
    'openConflictsView',
    (source: TimePanelSource, meeting: MeetingRequestMessageType) => ({ source, meeting })
);

/**
 * Closes Attendee Tracking view and associated Event Details view if currently open for the requested event
 *
 * Optionally closes Time Panel too, depending on the scenario and original panel entrypoint
 */
export const closeConflictsView = action('closeConflictsView');

/**
 * Opens Time Panel and navigates to Event Quick Compose view, with automatic handling of any existing or "dirty" compose forms
 *
 * Optionally pre-populates the form with content, with any un-specified values falling back to form defaults (if any)
 *
 * REMINDER: Verify panel availability first using isTimePanelAvailable util and consider implementing a graceful fallback,
 * e.g. launching event compose popout
 */
export const openEventQuickComposeView = action(
    'openEventQuickComposeView',
    (source: TimePanelSource, event?: Partial<CalendarEvent>) => ({
        source,
        event,
    })
);
